import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { CustomTextField } from "~/components";
import { AccessControlledButton } from "~/components/Buttons/AccessControlledButton";
import * as APITypes from "~/API";
import { dateToLocalTime, INTENTS, removeEmptyFields } from "~/utils";
import { useFetcher } from "react-router-dom";
import { useFetcherBusy } from "~/hooks/useRouteActivity";
import React from "react";

const DisplayMemberDetails = ({
  memberDetails,
}: {
  memberDetails: APITypes.User;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography.Title level={5}>{t("Basic_Information")}</Typography.Title>

      <Row gutter={16}>
        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Username")}
            </Typography.Text>
            <CustomTextField value={memberDetails.username || ""} />
          </Space>
        </Col>

        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Email")}
            </Typography.Text>
            <CustomTextField value={memberDetails.email || ""} />
          </Space>
        </Col>
      </Row>

      <br />

      <Row gutter={16}>
        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("First_Name")}
            </Typography.Text>
            <CustomTextField value={memberDetails.firstName || ""} />
          </Space>
        </Col>

        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Last_Name")}
            </Typography.Text>
            <CustomTextField value={memberDetails.lastName || ""} />
          </Space>
        </Col>
      </Row>

      <br />

      <Row gutter={16}>
        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Title")}
            </Typography.Text>
            <CustomTextField value={memberDetails.title || ""} />
          </Space>
        </Col>

        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Number")}
            </Typography.Text>
            <CustomTextField value={memberDetails.phoneNumber || ""} />
          </Space>
        </Col>
      </Row>

      <Divider />

      <Typography.Title level={5}>
        {t("Organization")} {t("Details")}
      </Typography.Title>

      <Row gutter={16}>
        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Role")}
            </Typography.Text>
            <CustomTextField value={memberDetails.roles || ""} />
          </Space>
        </Col>

        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Created_At")}
            </Typography.Text>
            <CustomTextField
              value={dateToLocalTime(memberDetails.createdAt, "dateTime")}
            />
          </Space>
        </Col>
      </Row>
    </>
  );
};

const EditMemberDetailsForm = ({
  memberDetails,
  canUpdateMemberRights,
  initialFormValues,
  formInstance,
  onFinish,
  isSubmitting,
  isSuperAdmin,
}: {
  memberDetails: APITypes.User;
  canUpdateMemberRights: boolean;
  initialFormValues: any;
  formInstance: any;
  onFinish: (values: any) => void;
  isSubmitting: boolean;
  isSuperAdmin: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Form
      initialValues={initialFormValues}
      layout="vertical"
      onFinish={onFinish}
      form={formInstance}
      disabled={isSubmitting}
    >
      <Typography.Title level={5}>{t("Basic_Information")}</Typography.Title>

      <Row gutter={16}>
        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Username")}
            </Typography.Text>
            <CustomTextField value={memberDetails.username || ""} />
          </Space>
        </Col>

        <Col span={12}>
          <Form.Item
            label={t("Email")}
            name="email"
            rules={[
              {
                type: "email",
                message: t("Please_enter_a_valid_email"),
              },
            ]}
          >
            <Input placeholder={t("Enter_a_contact_email")} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("First_Name")}
            </Typography.Text>
            <CustomTextField value={memberDetails.firstName || ""} />
          </Space>
        </Col>

        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Last_Name")}
            </Typography.Text>
            <CustomTextField value={memberDetails.lastName || ""} />
          </Space>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("Title")} name="title">
            <Input placeholder={t("Enter_a_title")} />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label={t("Number")} name="phoneNumber">
            <Input placeholder={t("Enter_a_number")} />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Typography.Title level={5}>
        {t("Organization")} {t("Details")}
      </Typography.Title>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("Role")} name="roles">
            <Select
              placeholder={t("Select_a_status")}
              disabled={!canUpdateMemberRights}
            >
              {Object.values(APITypes.ManagerRolesType).map((role) => (
                <Select.Option
                  key={role}
                  value={role}
                  disabled={
                    role === APITypes.ManagerRolesType.SuperAdmin ||
                    (role === APITypes.ManagerRolesType.Admin && !isSuperAdmin)
                  }
                >
                  {role}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Created_At")}
            </Typography.Text>
            <CustomTextField
              value={dateToLocalTime(memberDetails.createdAt, "dateTime")}
            />
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

export const MemberDetailsTab = ({
  memberDetails,
  canUpdateMemberRights,
  isSuperAdmin,
}: {
  memberDetails: APITypes.User;
  canUpdateMemberRights: boolean;
  isSuperAdmin: boolean;
}) => {
  const { t } = useTranslation();
  const [formInstance] = Form.useForm();
  const fetcher = useFetcher();
  const [showEditForm, setShowEditForm] = React.useState(false);
  const [showEditConfirm, setShowEditConfirm] = React.useState(false);
  const isSubmitting = useFetcherBusy(fetcher);

  const initialFormValues = {
    email: memberDetails.email || "",
    title: memberDetails.title || "",
    phoneNumber: memberDetails.phoneNumber || "",
    roles: memberDetails.roles || undefined,
  };

  const toggleEditForm = () => setShowEditForm(!showEditForm);
  const toggleEditConfirm = () => setShowEditConfirm(!showEditConfirm);

  const handleEditSubmit = (formValues: any) => {
    const editFormValues = removeEmptyFields(formValues);

    fetcher.submit(
      {
        body: JSON.stringify({
          ...editFormValues,
          _version: memberDetails._version,
        }),
        intent: INTENTS._edit_member_details,
      },
      { method: "POST" }
    );
    toggleEditForm();
  };

  const handleRemoveMember = () => {
    message.info("Remove member: Coming soon");
  };

  return (
    <>
      <Space
        style={{
          marginBottom: "1rem",
          marginTop: "1rem",
        }}
      >
        {showEditForm ? (
          <>
            <Popconfirm
              title={t("Confirm_changes")}
              onCancel={toggleEditConfirm}
              open={showEditConfirm}
              onConfirm={() => {
                formInstance.submit();
                toggleEditConfirm();
              }}
            >
              <Button
                type="primary"
                ghost
                onClick={() => toggleEditConfirm()}
                disabled={isSubmitting}
              >
                {t("Submit")}
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              ghost
              danger
              onClick={() => toggleEditForm()}
              disabled={isSubmitting}
            >
              {t("Cancel")}
            </Button>
          </>
        ) : (
          <AccessControlledButton
            canPerform={canUpdateMemberRights}
            label={t("Edit")}
            tooltipTitle={t("Only_Admin_Can_Edit_Member" as any) as string}
            submitting={isSubmitting}
            buttonProps={{
              type: "primary",
              ghost: true,
              disabled: showEditForm,
            }}
            onClick={() => toggleEditForm()}
          />
        )}

        <AccessControlledButton
          canPerform={canUpdateMemberRights}
          label={t("Remove_from_Organization")}
          tooltipTitle={t("Only_Admin_Can_Remove_Member" as any) as string}
          submitting={isSubmitting}
          buttonProps={{
            type: "primary",
            danger: true,
            disabled: showEditForm,
          }}
          confirm={{
            title: t("Remove_Member_Warning" as any) as string,
            okText: t("Remove"),
            okButtonProps: { danger: true },
            onConfirm: handleRemoveMember,
          }}
        />

      </Space>

      <div style={{ marginTop: "2rem" }}>
        {showEditForm ? (
          <EditMemberDetailsForm
            memberDetails={memberDetails}
            canUpdateMemberRights={canUpdateMemberRights}
            initialFormValues={initialFormValues}
            formInstance={formInstance}
            onFinish={handleEditSubmit}
            isSubmitting={isSubmitting}
            isSuperAdmin={isSuperAdmin}
          />
        ) : (
          <DisplayMemberDetails memberDetails={memberDetails} />
        )}
      </div>
    </>
  );
};
